import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { isEmpty, isString } from "lodash";
import { Fragment, useEffect, useState } from "react";
import { fetchImage } from "../../services/ api_services";
import NoImage from "../../assets/images/no_image_icon.png";

/** 비교 카드에 표시할 핵심 값. 전체 상세가 아니라 "나란히 놓고 봤을 때 의미 있는" 값만 고른다. */
const COMPARE_FIELDS = [
    { name: "항목", id: "spec_nm" },
    { name: "검사일자", id: "inst_dt" },
    { name: "촬영 환경", id: "description" },
    { name: "디바이스", id: "info_device_model" },
    { name: "밝기", id: "brightness" },
    { name: "측정된 RGB", id: "insp_rgb", swatch: true },
    { name: "보정된 RGB", id: "gray_scaled_rgb", swatch: true },
    { name: "Lab", id: "lab" },
    { name: "Cb", id: "cb" },
    { name: "Cr", id: "cr" },
    { name: "RGB거리", id: "distanceRGB" },
];

const rgbToCssColor = (rgb) => {
    if (rgb && isString(rgb)) {
        const parts = rgb.split(",");
        return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
    }
    return "transparent";
};

export default function CompareImagesDialog({ isOpen, onClose, rows }) {
    const [imageUrls, setImageUrls] = useState({}); // test_seq_num → objectURL
    const [loading, setLoading] = useState(false);
    const [zoomedSeq, setZoomedSeq] = useState(null); // 클릭해서 확대한 이미지

    useEffect(() => {
        if (!isOpen || isEmpty(rows)) return;

        let cancelled = false;
        const created = [];

        const loadImages = async () => {
            setLoading(true);
            const entries = await Promise.all(
                rows.map(async (row) => {
                    if (!row.file_path || !isString(row.file_path)) return [row.test_seq_num, null];
                    const path = row.file_path.replace(/^.*\\healthimage\\/, "");
                    try {
                        const response = await fetchImage(path);
                        if (response && response.status === 200) {
                            const url = URL.createObjectURL(response.data);
                            created.push(url);
                            return [row.test_seq_num, url];
                        }
                    } catch (e) {
                        console.log("e===>", e);
                    }
                    return [row.test_seq_num, null];
                })
            );
            if (cancelled) {
                created.forEach((url) => URL.revokeObjectURL(url));
                return;
            }
            setImageUrls(Object.fromEntries(entries));
            setLoading(false);
        };

        loadImages();

        // 다이얼로그가 닫히거나 대상이 바뀌면 생성한 objectURL 을 해제해 메모리 누수를 막는다
        return () => {
            cancelled = true;
            created.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [isOpen, rows]);

    const renderValue = (row, field) => {
        const raw = row[field.id];
        if (field.swatch) {
            return (
                <div className="flex items-center gap-x-1.5">
                    <span className="truncate tabular-nums">{raw || "-"}</span>
                    {raw && (
                        <span
                            className="h-4 w-4 shrink-0 rounded ring-1 ring-inset ring-black/10"
                            style={{ backgroundColor: rgbToCssColor(raw) }}
                        />
                    )}
                </div>
            );
        }
        return <span className="truncate">{raw === null || raw === undefined || raw === "" ? "-" : String(raw)}</span>;
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-hidden p-4 sm:p-6">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:scale-95"
                    >
                        <Dialog.Panel className="mx-auto flex h-full max-w-full flex-col overflow-hidden rounded-xl bg-slate-50 shadow-xl">
                            <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
                                <Dialog.Title as="h3" className="text-sm font-semibold text-gray-900">
                                    선택 비교
                                    <span className="ml-2 text-sm font-normal tabular-nums text-slate-500">
                                        {rows.length}건
                                    </span>
                                </Dialog.Title>
                                <button
                                    type="button"
                                    className="motion-press inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
                                    onClick={onClose}
                                >
                                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                    닫기
                                </button>
                            </div>

                            {/* 클릭 확대 — 아무 곳이나 클릭하면 닫힌다 */}
                            {zoomedSeq && imageUrls[zoomedSeq] && (
                                <div
                                    className="motion-fade-in fixed inset-0 z-[60] flex cursor-zoom-out items-center justify-center bg-black/80 p-6"
                                    onClick={() => setZoomedSeq(null)}
                                >
                                    <button
                                        type="button"
                                        className="motion-press absolute right-4 top-4 inline-flex items-center gap-x-1.5 rounded-md bg-white/90 px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-white"
                                        onClick={() => setZoomedSeq(null)}
                                    >
                                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                        닫기
                                    </button>
                                    <img
                                        src={imageUrls[zoomedSeq]}
                                        alt="검사 이미지 확대"
                                        className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
                                    />
                                </div>
                            )}

                            <div className="thin-scrollbar min-h-0 flex-1 overflow-auto p-4">
                                <div className="flex h-full min-h-0 gap-4">
                                    {rows.map((row) => (
                                        <div
                                            key={row.test_seq_num}
                                            className="flex w-72 shrink-0 flex-col overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200"
                                        >
                                            {/* 판정값 — 비교의 핵심이라 최상단 */}
                                            <div className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100">
                                                <div className="px-3 py-2">
                                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                                        모바일
                                                    </div>
                                                    <div className="text-lg font-bold tabular-nums text-gray-900">
                                                        {row.insp_level ?? "-"}
                                                    </div>
                                                </div>
                                                <div className="px-3 py-2">
                                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                                        검증
                                                    </div>
                                                    <div className="text-lg font-bold tabular-nums text-blue-600">
                                                        {row.calculate_lvl ?? "-"}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 이미지 */}
                                            <div
                                                className="flex h-56 shrink-0 cursor-zoom-in items-center justify-center bg-slate-50 p-2"
                                                title="클릭하면 원본 크기로 봅니다"
                                                onClick={() => imageUrls[row.test_seq_num] && setZoomedSeq(row.test_seq_num)}
                                            >
                                                {loading ? (
                                                    <span className="text-xs text-slate-400">불러오는 중…</span>
                                                ) : imageUrls[row.test_seq_num] ? (
                                                    <img
                                                        src={imageUrls[row.test_seq_num]}
                                                        alt="검사 이미지"
                                                        className="max-h-full max-w-full rounded-lg object-contain"
                                                    />
                                                ) : (
                                                    <div className="flex flex-col items-center gap-y-1 text-slate-400">
                                                        <img src={NoImage} alt="" className="h-12 w-12 opacity-60" />
                                                        <span className="text-xs">이미지 없음</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* 값 */}
                                            <dl className="thin-scrollbar min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto">
                                                {COMPARE_FIELDS.map((field) => (
                                                    <div key={field.id} className="flex items-center gap-x-2 px-3 py-1.5">
                                                        <dt className="w-20 shrink-0 text-[11px] font-semibold text-slate-500">
                                                            {field.name}
                                                        </dt>
                                                        <dd className="min-w-0 flex-1 text-xs text-gray-900">
                                                            {renderValue(row, field)}
                                                        </dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
